import type { AmsViewAdGroupMultiRow } from '@/client'
import type { AmsDetailTab } from '@/hooks/queries/amsView'

/**
 * The AMS View page's own types — the ones the generated client cannot give,
 * because they describe the page rather than the wire (VentaHub
 * `pages/ams_view/types.ts`; the row shapes it hand-wrote there now come from
 * `@/client`).
 */

/**
 * The per-ad-group tabs, in VentaHub's strip order minus Placements
 * (`AmsViewPage.tsx:91-104`; Placements is hidden by decision 2). The same
 * set the hooks key their drilled and aggregate queries by.
 */
export const DETAIL_TABS = [
  'asins',
  'targets',
  'search-terms',
  'negative-keywords',
  'alt-products',
] as const satisfies readonly AmsDetailTab[]

export type DetailTab = AmsDetailTab

/**
 * Every tab of the one strip (`AmsViewPage.tsx:106-118`): the two list tabs,
 * the detail tabs and Analytics, which scopes to the drilled ad group when
 * there is one and covers the whole report otherwise.
 */
export const PAGE_TABS = ['adgroups', 'campaigns', ...DETAIL_TABS, 'analytics'] as const

export type PageTab = (typeof PAGE_TABS)[number]

/** Whether a tab is one of the per-ad-group tabs (`AmsViewPage.tsx:106-108`). */
export function isDetailTab(tab: string | null | undefined): tab is DetailTab {
  return !!tab && (DETAIL_TABS as readonly string[]).includes(tab)
}

/**
 * The tab the page opens on when the URL names none: ASINs once an ad group
 * is drilled, the ad-group list otherwise (`AmsViewPage.tsx:1394-1400`).
 */
export function defaultTabFor(drilled: boolean): PageTab {
  return drilled ? 'asins' : 'adgroups'
}

/**
 * One row of the ad-group list (A5) with the store it was fetched for, which
 * VentaHub tagged on every row so a drill or a row chart knows which store to
 * ask (`AmsViewPage.tsx:120-122`, `types.ts:125-129`).
 */
export type AdGroupRow = AmsViewAdGroupMultiRow & { _source_store_id: number }

/** The page sizes every AMS View grid offers (`MetricTable.tsx:206`). */
export const PAGE_SIZES = [25, 50, 100, 250] as const
export type PageSize = (typeof PAGE_SIZES)[number]

/** The rows a grid shows before anyone picks a size (`MetricTable.tsx:205`). */
export const DEFAULT_PAGE_SIZE: PageSize = 50

/**
 * The state of the grid on screen — page (1-based), page size, the expanded
 * row, the sort column and direction and the grid's own search box. VentaHub
 * held it in each table (`MetricTable.tsx:213-222`); here it is URL state
 * (`page`, `size`, `open`, `gs`, `gd`, `gq`), so a link reopens the same row.
 */
export type GridState = {
  page: number
  size: PageSize
  open?: string
  sort?: string
  dir?: 'asc' | 'desc'
  q?: string
}

/** A change to the grid state; keys present with `undefined` clear that key. */
export type GridChange = (patch: Partial<GridState>) => void

/**
 * The request scope the page has applied, built by the page from its URL and
 * handed to every tab and export (`AmsViewPage.tsx:726-760, 939-982, 1904-1931`).
 */
export type AmsScope = {
  /** Ascending; empty means there is no Amazon store to ask, so nothing is requested. */
  storeIds: number[]
  /** `YYYY-MM-DD`, both inclusive. */
  start: string
  end: string
  /** The brand filter as the API takes it; `undefined` sends none. */
  brandIds: number[] | undefined
  /** The applied search box. */
  search: string | undefined
  /** The second filter row's live lists. */
  campaigns: string[]
  statuses: string[]
  types: string[]
  targetings: string[]
  /**
   * The ad groups the second row left standing, or `null` when it removed
   * none (`AmsViewPage.tsx:972-982`); `[]` is meaningful and means no rows.
   */
  aggregateAdGroupIds: number[] | null
}
